import React, { useEffect, useMemo, useState } from "react";

const APP_PACKAGE = "com.complymax.app";

const DigilockerCallbackBridge = () => {
  // Read all params from the DigiLocker redirect URL
  const { status, clientId, customUrl, intentUrl, preferredUrl, webProfileUrl } =
    useMemo(() => {
      const scheme = String(
        process.env.REACT_APP_APP_SCHEME || "com.complymax.app"
      )
        .replace(/:\/+$/, "")
        .trim();

      const search = window.location.search || "";
      const hash = window.location.hash || "";
      const params = search || (hash ? `?${hash.replace(/^#/, "")}` : "");

      const urlParams = new URLSearchParams(
        search || hash.replace(/^#/, "")
      );
      const verificationStatus = urlParams.get("status") || "success";
      const extractedClientId =
        urlParams.get("client_id") ||
        urlParams.get("clientId") ||
        urlParams.get("reference_id") ||
        "";

      // Custom URL scheme — works in WebViews and non-Chrome browsers
      const custom = `${scheme}://digilocker-callback${params}`;

      // intent:// — Chrome on Android's native format for triggering deep links from browser.
      // S.browser_fallback_url tells Chrome where to go if the app is not installed.
      const fallbackEncoded = encodeURIComponent(window.location.href);
      const intent = `intent://digilocker-callback${params}#Intent;scheme=${scheme};package=${APP_PACKAGE};S.browser_fallback_url=${fallbackEncoded};end`;

      // Chrome on Android (not WebView) handles intent:// natively
      const isAndroidChrome =
        /android/i.test(navigator.userAgent) &&
        /chrome/i.test(navigator.userAgent) &&
        !/wv/i.test(navigator.userAgent);

      return {
        status: verificationStatus,
        clientId: extractedClientId,
        customUrl: custom,
        intentUrl: intent,
        preferredUrl: isAndroidChrome ? intent : custom,
        // Web fallback: if no app installed, go to profile page directly in browser
        webProfileUrl: `/user_dashboard/user_profile${search}`,
      };
    }, []);

  const isSuccess = status === "success";
  const [showManualOpen, setShowManualOpen] = useState(false);
  const [isOpeningApp, setIsOpeningApp] = useState(isSuccess);

  useEffect(() => {
    // Store client_id so the web profile page can pick it up if the user stays in browser
    if (clientId) {
      localStorage.setItem("digilocker_client_id", clientId);
    }
    localStorage.setItem("digilocker_verification_status", isSuccess ? "success" : status);
  }, [clientId, isSuccess, status]);

  useEffect(() => {
    if (!isSuccess) return;

    let closed = false;
    let iframe = null;

    const openApp = () => {
      if (closed) return;
      try {
        window.location.assign(preferredUrl);
      } catch (_) {}

      // Iframe secondary attempt — helps in browsers that block window.location for custom schemes
      if (!iframe) {
        iframe = document.createElement("iframe");
        iframe.style.cssText = "display:none;width:0;height:0;border:0;";
        iframe.src = customUrl;
        document.body.appendChild(iframe);
      } else {
        iframe.src = customUrl;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Page went to background — app opened successfully
        closed = true;
        setIsOpeningApp(false);
        setShowManualOpen(false);
      }
    };

    openApp();

    const retryTimer = setTimeout(() => {
      if (!closed && document.visibilityState === "visible") openApp();
    }, 1200);

    const fallbackTimer = setTimeout(() => {
      if (!closed && document.visibilityState === "visible") {
        setIsOpeningApp(false);
        setShowManualOpen(true);

        // After showing the manual button, auto-redirect to web profile
        // so users without the app can still complete the flow in browser
        setTimeout(() => {
          if (!closed && document.visibilityState === "visible") {
            window.location.replace(webProfileUrl);
          }
        }, 4000);
      }
    }, 2500);

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      closed = true;
      clearTimeout(retryTimer);
      clearTimeout(fallbackTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (iframe?.parentNode) iframe.parentNode.removeChild(iframe);
    };
  }, [isSuccess, preferredUrl, customUrl, webProfileUrl]);

  const handleManualOpen = (e) => {
    e.preventDefault();
    setIsOpeningApp(true);
    setShowManualOpen(false);
    window.location.assign(preferredUrl);
  };

  // ── Failure screen ──────────────────────────────────────────────────────────
  if (!isSuccess) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.iconWrap}>
            <svg viewBox="0 0 52 52" style={styles.icon} fill="none">
              <circle cx="26" cy="26" r="26" fill="#fdecea" />
              <path
                d="M20 20l12 12M32 20l-12 12"
                stroke="#c62828"
                strokeWidth="3.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <h1 style={{ ...styles.heading, color: "#c62828" }}>
            Verification Failed
          </h1>
          <p style={styles.body}>
            DigiLocker verification was not completed successfully. Please go
            back to the Complymax app and try again.
          </p>
          <a
            href={customUrl}
            onClick={handleManualOpen}
            style={{ ...styles.button, background: "#c62828" }}
          >
            Return To App
          </a>
        </div>
      </div>
    );
  }

  // ── Success screen ──────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.iconWrap}>
          <svg viewBox="0 0 52 52" style={styles.icon} fill="none">
            <circle cx="26" cy="26" r="26" fill="#e6f4ea" />
            <path
              d="M15 27l8 8 14-14"
              stroke="#2e7d32"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1 style={styles.heading}>Verification Successful</h1>

        <p style={styles.body}>
          Your Aadhaar identity has been verified successfully via DigiLocker.
          We are taking you back to the Complymax app so you can continue.
        </p>

        {isOpeningApp && !showManualOpen && (
          <p style={styles.redirecting}>Opening the app&hellip;</p>
        )}

        {showManualOpen && (
          <>
            <p style={styles.fallbackNote}>
              Verification is complete. If the app did not open automatically,
              tap below to go back to the Complymax app.
            </p>
            <a href={preferredUrl} onClick={handleManualOpen} style={styles.button}>
              Go Back To The App
            </a>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f5f7fa",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    padding: "24px",
    boxSizing: "border-box",
  },
  card: {
    background: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
    padding: "40px 32px",
    maxWidth: "420px",
    width: "100%",
    textAlign: "center",
  },
  iconWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "24px",
  },
  icon: { width: "72px", height: "72px" },
  heading: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#1a1a2e",
    margin: "0 0 12px",
  },
  body: {
    fontSize: "15px",
    color: "#4a5568",
    lineHeight: "1.6",
    margin: "0 0 24px",
  },
  redirecting: {
    fontSize: "14px",
    color: "#718096",
    margin: "0",
    fontStyle: "italic",
  },
  fallbackNote: {
    fontSize: "14px",
    color: "#718096",
    lineHeight: "1.5",
    margin: "0 0 20px",
  },
  button: {
    display: "inline-block",
    background: "#1565c0",
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: "600",
    padding: "14px 32px",
    borderRadius: "10px",
    textDecoration: "none",
    letterSpacing: "0.3px",
  },
};

export default DigilockerCallbackBridge;
